import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Award, Download, CheckCircle2, GraduationCap } from 'lucide-react';
import { toThumbnailUrl } from '@/utils/drive';

export default function MyDiplomas() {
  const navigate = useNavigate();
  const [diplomas, setDiplomas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getMyDiplomas()
      .then((r) => setDiplomas(r.data))
      .catch(() => toast.error('Error al cargar diplomas'))
      .finally(() => setLoading(false));
  }, []);

  const fmt = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl" data-testid="my-diplomas-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight flex items-center gap-3">
            <Award className="h-7 w-7 text-[#DC143C]" />
            Mis Diplomas
          </h1>
          <p className="text-gray-600 mt-1">Galería de cursos completados y sus diplomas</p>
        </div>

        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">
            Cargando...
          </div>
        ) : diplomas.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">
            <GraduationCap className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="mb-4">Aún no tienes diplomas. Completa tus cursos en Mi Academia para obtenerlos.</p>
            <Button onClick={() => navigate('/academia')} variant="outline">Ir a Mi Academia</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {diplomas.map((d) => {
              // Prioridad para el preview de la tarjeta:
              // 1) template_url del diploma (vía proxy si es Drive)
              // 2) thumbnail_url del curso (vía toThumbnailUrl si es Drive)
              const apiBase = process.env.REACT_APP_BACKEND_URL;
              const previewSrc = d.template_url
                ? `${apiBase}/api/diploma/proxy-image?url=${encodeURIComponent(d.template_url)}`
                : (d.thumbnail_url ? toThumbnailUrl(d.thumbnail_url, 480) : null);
              return (
              <div key={d.course_id} className="bg-white rounded-lg shadow-sm border-2 border-green-200 overflow-hidden hover:shadow-md transition-shadow" data-testid="diploma-card">
                <div className="relative">
                  {previewSrc ? (
                    <img src={previewSrc} alt={d.course_title} className="w-full h-40 object-cover bg-gray-100" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-full h-40 bg-gradient-to-br from-green-600 to-green-700 flex items-center justify-center">
                      <Award className="h-16 w-16 text-white/40" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    {d.score}%
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">{d.course_title}</h3>
                  <p className="text-sm text-gray-500 mb-4">Completado el {fmt(d.completed_at)}</p>
                  <Button
                    onClick={() => navigate(`/academia/certificado/${d.course_id}`)}
                    data-testid="view-diploma-button"
                    className="w-full bg-[#DC143C] hover:bg-[#B91C1C] text-white flex items-center justify-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Ver / Descargar Diploma
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
